import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CloseIcon from '@mui/icons-material/Close';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslate } from 'react-admin';
import { RichTextInput, RichTextInputToolbar } from 'ra-input-rich-text';
import { apiPost } from '../../services/apiClient';
import {
  SMS_PROVIDER_FOOTER_CHARS,
  SMS_SEGMENT_CHARS,
  smsSegmentCount,
} from '../../utils/smsLimits';

export const PUSH_REASON_MAX_CHARS = 200;

const ChannelCard = memo(({
  icon,
  title,
  subtitle,
  enabled = true,
  onEnabledChange,
  previewOpen,
  onTogglePreview,
  children,
  preview,
  inlinePreview = true,
  previewActive = false,
  previewTooltip = 'Önizle — müşteriye nasıl gidecek',
}) => (
  <Paper
    elevation={0}
    sx={{
      border: '1px solid',
      borderColor: previewActive || previewOpen ? 'primary.main' : 'divider',
      borderRadius: 2,
      overflow: 'hidden',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      opacity: enabled ? 1 : 0.55,
    }}
  >
    <Box
      sx={{
        px: 2,
        py: 1.25,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        bgcolor: 'action.hover',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      {icon}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" fontWeight={700}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary" display="block">
            {subtitle}
          </Typography>
        )}
      </Box>
      {typeof onEnabledChange === 'function' && (
        <FormControlLabel
          sx={{ mr: 0.5, ml: 0 }}
          control={
            <Checkbox
              size="small"
              checked={enabled}
              onChange={(e) => onEnabledChange(e.target.checked)}
            />
          }
          label={
            <Typography variant="caption" color="text.secondary">
              Gönder
            </Typography>
          }
        />
      )}
      <Tooltip title={previewTooltip}>
        <IconButton
          size="small"
          color={previewActive || previewOpen ? 'primary' : 'default'}
          onClick={onTogglePreview}
          aria-label="önizleme"
          disabled={!enabled}
        >
          {inlinePreview && previewOpen ? (
            <VisibilityOffIcon fontSize="small" />
          ) : (
            <VisibilityIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
    </Box>

    <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <fieldset
        disabled={!enabled}
        style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}
      >
        {children}
      </fieldset>
      {inlinePreview && previewOpen && enabled && (
        <Box
          sx={{
            mt: 'auto',
            pt: 1.5,
            borderTop: '1px dashed',
            borderColor: 'divider',
          }}
        >
          <Typography variant="caption" color="primary.main" fontWeight={600} display="block" sx={{ mb: 1 }}>
            Önizleme
          </Typography>
          {preview}
        </Box>
      )}
    </Box>
  </Paper>
));

ChannelCard.displayName = 'ChannelCard';

const PreviewLoading = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2, justifyContent: 'center' }}>
    <CircularProgress size={22} />
    <Typography variant="body2" color="text.secondary">
      PUS şablonu yükleniyor…
    </Typography>
  </Box>
);

const PreviewError = ({ message }) => (
  <Typography variant="body2" color="error.main" sx={{ py: 2 }}>
    {message || 'Önizleme alınamadı'}
  </Typography>
);

const MailPreview = ({ subject, bodyHtml, error, loading, fullSize = false }) => {
  if (loading) return <PreviewLoading />;
  if (error) return <PreviewError message={error} />;
  return (
    <Box
      sx={{
        bgcolor: '#fff',
        border: '1px solid #e0e0e0',
        borderRadius: 1,
        overflow: 'hidden',
        fontSize: 14,
        ...(fullSize
          ? { minHeight: 320 }
          : { maxHeight: 280, overflowY: 'auto' }),
      }}
    >
      <Box sx={{ px: 2, py: 1.5, bgcolor: '#f5f5f5', borderBottom: '1px solid #eee' }}>
        <Typography variant="caption" color="text.secondary" display="block">
          Konu
        </Typography>
        <Typography variant="body1" fontWeight={600}>
          {subject || '—'}
        </Typography>
      </Box>
      <Box
        sx={{
          p: 2,
          '& p': { m: 0, mb: 1.5 },
          '& img': { maxWidth: '100%', height: 'auto' },
          color: 'text.primary',
          ...(fullSize ? { overflowY: 'auto', maxHeight: '70vh' } : {}),
        }}
        dangerouslySetInnerHTML={{ __html: bodyHtml || '<p>—</p>' }}
      />
    </Box>
  );
};

const SmsPreview = ({
  text,
  error,
  loading,
  initialLoad,
}) => {
  if (loading && initialLoad) return <PreviewLoading />;
  if (error) return <PreviewError message={error} />;
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
        <Box
          sx={{
            maxWidth: '100%',
            bgcolor: '#e8f5e9',
            color: '#1b5e20',
            px: 1.5,
            py: 1,
            borderRadius: 2,
            borderTopLeftRadius: 4,
            fontSize: 13,
            lineHeight: 1.45,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {text || '—'}
        </Box>
      </Box>
    </Box>
  );
};

const PushPreview = ({ title, body, isEventCancel, error, loading, initialLoad }) => {
  if (loading && initialLoad) return <PreviewLoading />;
  if (error) return <PreviewError message={error} />;
  return (
    <Box
      sx={{
        bgcolor: '#fafafa',
        border: '1px solid #ddd',
        borderRadius: 2,
        p: 1.5,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: 1,
            bgcolor: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: 11,
            fontWeight: 700,
          }}
        >
          GK
        </Box>
        <Typography variant="caption" color="text.secondary">
          Gişe Kıbrıs · şimdi
        </Typography>
      </Box>
      <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.3 }}>
        {title || '—'}
      </Typography>
      {isEventCancel && (
        <Typography variant="caption" color="error.main" display="block">
          Etkinlik iptali
        </Typography>
      )}
      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line', mt: 0.5 }}>
        {body || '—'}
      </Typography>
    </Box>
  );
};

/**
 * İade / etkinlik iptali bildirim alanları — mail, SMS, push.
 */
const RefundNotificationFields = ({
  reasonMail,
  reasonSms,
  reasonPush,
  onMailChange,
  onSmsChange,
  onPushChange,
  sendMail = true,
  sendSms = true,
  sendPush = true,
  onSendMailChange,
  onSendSmsChange,
  onSendPushChange,
  eventName,
  isEventCancel = false,
  previewAmount,
  previewPnr,
}) => {
  const translate = useTranslate();
  const [mailPreviewModalOpen, setMailPreviewModalOpen] = useState(false);
  const [showSmsPreview, setShowSmsPreview] = useState(false);
  const [showPushPreview, setShowPushPreview] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const debounceRef = useRef(null);
  const onMailChangeRef = useRef(onMailChange);
  const previewEverLoaded = useRef(false);

  useEffect(() => {
    onMailChangeRef.current = onMailChange;
  }, [onMailChange]);

  const form = useForm({
    defaultValues: { mailReason: reasonMail || '' },
  });

  useEffect(() => {
    const sub = form.watch((values) => {
      onMailChangeRef.current?.(values.mailReason || '');
    });
    return () => sub.unsubscribe();
  }, [form]);

  const eventKey = isEventCancel ? 'event_cancelled' : 'refund_success';

  const previewPayload = useMemo(
    () => ({
      eventKey,
      eventName: eventName || undefined,
      amount:
        previewAmount != null && previewAmount !== ''
          ? Number(previewAmount)
          : undefined,
      pnr: previewPnr || undefined,
      refundReasonMail: reasonMail || undefined,
      refundReasonSms: reasonSms || undefined,
      refundReasonPush: reasonPush || undefined,
    }),
    [
      eventKey,
      eventName,
      previewAmount,
      previewPnr,
      reasonMail,
      reasonSms,
      reasonPush,
    ],
  );

  const previewEnabled =
    mailPreviewModalOpen || showSmsPreview || showPushPreview;

  useEffect(() => {
    if (!previewEnabled) return undefined;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const isFirst = !previewEverLoaded.current;
      if (isFirst) setPreviewLoading(true);
      setPreviewError(null);
      try {
        const data = await apiPost('notifications/transactional-preview', previewPayload);
        setPreview(data);
        previewEverLoaded.current = true;
      } catch (e) {
        setPreview(null);
        setPreviewError(e?.message || 'Önizleme alınamadı');
      } finally {
        if (isFirst) setPreviewLoading(false);
      }
    }, 700);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [previewPayload, previewEnabled]);

  const toggleSmsPreview = useCallback(() => {
    setShowSmsPreview((v) => !v);
  }, []);

  const togglePushPreview = useCallback(() => {
    setShowPushPreview((v) => !v);
  }, []);

  const openMailPreview = useCallback(() => {
    setMailPreviewModalOpen(true);
  }, []);

  const mailPreviewProps = {
    loading: previewLoading,
    error: preview?.mail?.error || previewError,
    subject: preview?.mail?.subject,
    bodyHtml: preview?.mail?.bodyHtml,
  };

  const smsPreviewProps = {
    loading: previewLoading,
    initialLoad: !previewEverLoaded.current,
    error: preview?.sms?.error || previewError,
    text: preview?.sms?.text,
  };

  const pushPreviewProps = {
    loading: previewLoading,
    initialLoad: !previewEverLoaded.current,
    error: previewError,
    title: preview?.push?.title,
    body: preview?.push?.body,
    isEventCancel,
  };

  const reasonSmsSegments = smsSegmentCount(reasonSms.length);

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
        {translate('notifications.refund_fields_title', { _: 'Bildirim metinleri' })}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {translate('notifications.refund_fields_desc', {
          _: 'Tik olan kanallar gider. Mail/SMS PUS şablonundan; push Gişe API üzerinden.',
        })}
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <ChannelCard
            icon={<EmailOutlinedIcon color="primary" fontSize="small" />}
            title={translate('notifications.channel_email', { _: 'E-posta' })}
            subtitle="PUS mail"
            enabled={sendMail}
            onEnabledChange={onSendMailChange}
            inlinePreview={false}
            previewActive={mailPreviewModalOpen}
            previewOpen={false}
            onTogglePreview={openMailPreview}
            previewTooltip={translate('notifications.email_preview_tooltip', { _: 'Tam mail önizlemesi — modalda aç' })}
          >
            <FormProvider {...form}>
              <RichTextInput
                source="mailReason"
                label={false}
                fullWidth
                toolbar={<RichTextInputToolbar size="small" />}
              />
            </FormProvider>
            <Typography variant="caption" color="text.secondary">
              {translate('notifications.default_text_hint', { _: 'Boş bırakılırsa varsayılan iptal/iade metni gider.' })}
            </Typography>
          </ChannelCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <ChannelCard
            icon={<SmsOutlinedIcon color="success" fontSize="small" />}
            title="SMS"
            subtitle={`${SMS_SEGMENT_CHARS} karakter ≈ 1 SMS (limit yok)`}
            enabled={sendSms}
            onEnabledChange={onSendSmsChange}
            previewOpen={showSmsPreview}
            onTogglePreview={toggleSmsPreview}
            preview={<SmsPreview {...smsPreviewProps} />}
          >
            <TextField
              label={translate('notifications.sms_label', { _: 'SMS metni (ek cümle)' })}
              value={reasonSms}
              onChange={(e) => onSmsChange?.(e.target.value)}
              fullWidth
              size="small"
              multiline
              minRows={4}
              helperText={
                `Ek metin ${reasonSms.length} karakter · tahmini ${reasonSmsSegments} SMS (sağlayıcı eki ~${SMS_PROVIDER_FOOTER_CHARS} dahil)`
              }
            />
          </ChannelCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <ChannelCard
            icon={<NotificationsOutlinedIcon color="warning" fontSize="small" />}
            title="Push"
            subtitle={`Max ${PUSH_REASON_MAX_CHARS} char`}
            enabled={sendPush}
            onEnabledChange={onSendPushChange}
            previewOpen={showPushPreview}
            onTogglePreview={togglePushPreview}
            preview={<PushPreview {...pushPreviewProps} />}
          >
            <TextField
              label={translate('notifications.push_label', { _: 'Push gövde metni' })}
              value={reasonPush}
              onChange={(e) => onPushChange?.(e.target.value)}
              fullWidth
              size="small"
              multiline
              minRows={4}
              inputProps={{ maxLength: PUSH_REASON_MAX_CHARS }}
              helperText={`${reasonPush.length}/${PUSH_REASON_MAX_CHARS}`}
            />
          </ChannelCard>
        </Grid>
      </Grid>

      <Dialog
        open={mailPreviewModalOpen}
        onClose={() => setMailPreviewModalOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ pr: 6 }}>
          {translate('notifications.email_preview_title', { _: 'E-posta önizleme' })}
          <IconButton
            aria-label="kapat"
            onClick={() => setMailPreviewModalOpen(false)}
            sx={{ position: 'absolute', right: 12, top: 12 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: '#f9fafb', p: 2 }}>
          <MailPreview {...mailPreviewProps} fullSize />
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button onClick={() => setMailPreviewModalOpen(false)} variant="contained">
            {translate('ra.action.close', { _: 'Kapat' })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default memo(RefundNotificationFields);
